/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { FormValue, Question, OASISFormData } from '../../types';
import type { BespokeFormSectionProps } from '../bespokeRegistry';

interface BespokeSectionContextValue {
  answers: Record<string, FormValue>;
  onAnswerChange: (qid: string, value: FormValue) => void;
  onFormDataChange: (updates: Partial<OASISFormData>) => void;
  schemaMap: Map<string, Question>;
}

const BespokeSectionContext = createContext<BespokeSectionContextValue | null>(
  null
);

export function useBespokeSectionContext(): BespokeSectionContextValue {
  const ctx = useContext(BespokeSectionContext);
  if (!ctx)
    throw new Error(
      'OASIS primitives must be used inside <BespokeSectionProvider>'
    );
  return ctx;
}

type BespokeSectionProviderProps = BespokeFormSectionProps & {
  children: React.ReactNode;
};

function buildSchemaMap(questions: Question[]): Map<string, Question> {
  const map = new Map<string, Question>();
  const add = (qs: Question[]) => {
    for (const q of qs) {
      map.set(q.qid, q);
      if (q.subitems) {
        for (const subqs of Object.values(q.subitems)) add(subqs);
      }
    }
  };
  add(questions);
  return map;
}

export function BespokeSectionProvider({
  answers,
  onAnswerChange,
  onFormDataChange,
  schema,
  children,
}: BespokeSectionProviderProps) {
  const schemaMap = useMemo(() => buildSchemaMap(schema), [schema]);

  const value = useMemo(
    () => ({ answers, onAnswerChange, onFormDataChange, schemaMap }),
    [answers, onAnswerChange, onFormDataChange, schemaMap]
  );

  return (
    <BespokeSectionContext.Provider value={value}>
      {children}
    </BespokeSectionContext.Provider>
  );
}
